import { useMemo } from 'react';

import {
  type ReadOrganizationWithEmployees,
  type ReadPersonWithRelationships,
  useAllPartiesWithRelationshipsQuery,
} from '@/services/api/partiesService';
import {
  type ReadAnimal,
  type ReadPropertyWithOwners,
  type ReadVehicleWithOccupants,
  useAllSubjectsWithRelationshipsQuery,
} from '@/services/api/subjectsService';

interface VehicleGroup {
  vehicle: ReadVehicleWithOccupants;
  occupants: ReadPersonWithRelationships[];
}

interface PropertyGroup {
  property: ReadPropertyWithOwners;
}

interface IncidentSceneData {
  claimantPerson: ReadPersonWithRelationships | null;
  claimantOrg: ReadOrganizationWithEmployees | null;
  vehicleGroups: VehicleGroup[];
  propertyGroups: PropertyGroup[];
  unaffiliatedPersons: ReadPersonWithRelationships[];
  unaffiliatedOrgs: ReadOrganizationWithEmployees[];
  animals: ReadAnimal[];
  isEmpty: boolean;
}

export const useIncidentScene = (matterId: string) => {
  const { data: parties, isLoading: partiesLoading } =
    useAllPartiesWithRelationshipsQuery({ matterId });
  const { data: subjects, isLoading: subjectsLoading } =
    useAllSubjectsWithRelationshipsQuery({ matterId });

  const isLoading = partiesLoading || subjectsLoading;

  const sceneData = useMemo<IncidentSceneData | null>(() => {
    if (!parties || !subjects) return null;

    const claimantPerson = parties.persons.find((p) => p.is_claimant) || null;
    const claimantOrg =
      parties.organizations.find((o) => o.is_claimant) || null;

    const occupiedPersonIds = new Set(
      subjects.vehicles.flatMap(
        (v) => v.occupants?.map((o) => o.person_id) || [],
      ),
    );

    const vehicleGroups: VehicleGroup[] = subjects.vehicles.map((vehicle) => ({
      vehicle,
      occupants:
        vehicle.occupants
          ?.map((occ) => parties.persons.find((p) => p.id === occ.person_id))
          .filter((p): p is ReadPersonWithRelationships => p !== undefined) ||
        [],
    }));

    const propertyGroups: PropertyGroup[] = subjects.properties.map(
      (property) => ({
        property,
      }),
    );

    const unaffiliatedPersons = parties.persons.filter(
      (p) => !p.is_claimant && !occupiedPersonIds.has(p.id),
    );

    const unaffiliatedOrgs = parties.organizations.filter(
      (o) => !o.is_claimant,
    );

    const isEmpty =
      !claimantPerson &&
      !claimantOrg &&
      vehicleGroups.length === 0 &&
      propertyGroups.length === 0 &&
      unaffiliatedPersons.length === 0 &&
      unaffiliatedOrgs.length === 0 &&
      subjects.animals.length === 0;

    return {
      claimantPerson,
      claimantOrg,
      vehicleGroups,
      propertyGroups,
      unaffiliatedPersons,
      unaffiliatedOrgs,
      animals: subjects.animals,
      isEmpty,
    };
  }, [parties, subjects]);

  return { sceneData, isLoading };
};
